import React, { useEffect, useRef, useState } from "react";
import { Camera } from "react-camera-pro";
import { LoadingState, TailwindModal } from "../../../components";
import {
    base64ToBlob,
    blobToFile,
    useFileUpload,
    useStore,
} from "../../../helpers";
import { observer } from "mobx-react-lite";
import {
    IconZoomScan,
    IconUpload,
    IconPhoto,
    IconAlertCircle,
} from "@tabler/icons-react";
import { useParams } from "react-router";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const defaultMessages = {
    noCameraAccessible:
        "No camera device accessible. Please connect your camera or try a different browser.",
    permissionDenied:
        "Permission denied. Please refresh and give camera permission.",
    switchCamera:
        "Cannot switch camera because only one video device is accessible.",
    canvas: "Canvas is not supported.",
};

const ImageCapture = () => {
    const camera = useRef(null);
    const fileInputRef = useRef(null);
    const { t } = useTranslation("fetchFromFacialRecognition");

    const {
        aiGentoolStore: { image_capture_modal, setImage, captured_image },
        aiGentoolStore,
        voterListStore,
    } = useStore();

    const { projectid } = useParams();
    const { isUploading, uploadFileToS3 } = useFileUpload();

    const [cameraAllowed, setCameraAllowed] = useState(true);

    useEffect(() => {
        setImage(null);

        // check if camera is available
        navigator.mediaDevices
            ?.getUserMedia({ video: true })
            .then(() => setCameraAllowed(true))
            .catch(() => setCameraAllowed(false));
    }, [image_capture_modal]);

    const handleSubmit = async () => {
        if (!captured_image) return;

        const blob = base64ToBlob(captured_image);
        const file = blobToFile(blob, `captured_image_${Date.now()}.jpeg`);
        const { key } = await uploadFileToS3(
            `project-${projectid}/facial-recognition`,
            file
        );

        try {
            aiGentoolStore.toggle_image_capture_modal(false);
            aiGentoolStore.toggle_image_match_modal(true);

            const response = await voterListStore.getScannedVoter({
                projectid,
                s3_link: key,
            });

            console.log("Scanned voter response:", response);

            aiGentoolStore.toggle_image_match_modal(false);

            if (response?.status === 200) {
                const payload = response?.data?.payload;
                if (Array.isArray(payload) && payload.length > 0) {
                    aiGentoolStore.setScannedVoters(payload);
                    aiGentoolStore.update_show_scanned_voter(true);
                } else {
                    aiGentoolStore.show_voter_not_found(true);
                }
            } else if (response?.status === 422) {
                aiGentoolStore.show_multiple_faces_detected(true);
            } else {
                aiGentoolStore.show_server_error(true);
            }
        } catch (error) {
            console.error("Face recognition error:", error);
            aiGentoolStore.toggle_image_match_modal(false);
            aiGentoolStore.show_server_error(true);
        }
    };

    const handleImageCaptureClose = () => {
        aiGentoolStore.toggle_image_capture_modal(false);
    };

    const handleCaptureImage = () => {
        try {
            if (captured_image) {
                setImage(null);
            } else {
                setImage(camera?.current?.takePhoto());
            }
        } catch (error) {
            toast.error(error?.["message"]);
        }
    };

    const handleFileUpload = (event) => {
        const file = event.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                setImage(e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    if (isUploading) {
        return <LoadingState />;
    }

    return (
        <TailwindModal
            isOpen={image_capture_modal}
            onClose={handleImageCaptureClose}
        >
            <div className="p-4 flex flex-col items-center bg-white rounded-xl max-w-4xl h-[85vh] border border-dashed border-purple-400">
                <div className="flex justify-between items-center w-full mb-9">
                    <h2 className="text-lg font-semibold text-gray-800">
                        {t("capture")}
                    </h2>
                    <button
                        onClick={handleImageCaptureClose}
                        className="text-gray-500 hover:text-gray-700 text-xl"
                    >
                        ✕
                    </button>
                </div>

                <div className="relative flex-1 h-[55vh] w-full">
                    {captured_image ? (
                        <div className="relative h-full w-full overflow-hidden">
                            <img
                                src={captured_image}
                                className="w-full h-full object-cover rounded-lg shadow-md scale-x-[-1]"
                                alt="Taken or uploaded"
                            />
                        </div>
                    ) : (
                        <div className="relative h-full w-full overflow-hidden">
                            {cameraAllowed ? (
                                <>
                                    <div className="scan-line"></div>
                                    <Camera
                                        ref={camera}
                                        errorMessages={defaultMessages}
                                        aspectRatio={16 / 10}
                                        facingMode="user"
                                    />
                                </>
                            ) : (
                                <div className="flex flex-col justify-center items-center h-full text-center p-6 bg-red-50 rounded-lg border border-red-300">
                                    <IconAlertCircle
                                        size={40}
                                        className="text-red-600 mb-3"
                                    />
                                    <p className="text-red-700 font-medium mb-2">
                                        {t("camera_access_blocked_title")}
                                    </p>

                                    <p className="text-gray-600 text-sm mb-4">
                                        {t("camera_access_message")}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Buttons section */}
                    {cameraAllowed && (
                        <div className="absolute bottom-4 w-full flex justify-center gap-4">
                            {/* Take/Retake Photo */}
                            <button
                                className="bg-[#6D4DFF] px-4 py-2 rounded-xl text-white shadow-md hover:bg-[#5a3ce0] transition flex items-center gap-2"
                                onClick={handleCaptureImage}
                            >
                                <IconZoomScan size={20} />
                                {captured_image ? t("retake") : t("take_photo")}
                            </button>

                            <button
                                className="bg-blue-600 px-4 py-2 rounded-xl text-white shadow-md hover:bg-blue-700 transition flex items-center gap-2"
                                onClick={() => fileInputRef.current.click()}
                            >
                                <IconPhoto size={20} />
                                {captured_image ? t("reupload") : t("upload")}
                            </button>
                            <input
                                type="file"
                                accept="image/*"
                                ref={fileInputRef}
                                onChange={handleFileUpload}
                                className="hidden"
                            />
                            {captured_image && (
                                <button
                                    className="bg-green-600 px-4 py-2 rounded-xl text-white shadow-md hover:bg-green-700 transition flex items-center gap-2"
                                    onClick={handleSubmit}
                                >
                                    <IconUpload size={20} />
                                    {t("submit")}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </TailwindModal>
    );
};

export default observer(ImageCapture);
